import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CRMLayout } from "@/components/CRMLayout";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { AlertTriangle, Brain, Check, CheckSquare2, ExternalLink, Loader2, Mail, Pencil, X, Zap } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const euros = (v) => (Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function DetailAction({ action }) {
  if (action.type === "od") {
    const lignes = action.lignes || [];
    return (
      <div className="mt-2 overflow-x-auto">
        <p className="text-xs text-slate-500">{action.societe} · {action.date} · journal {action.journal || "OD"} · {action.ref}</p>
        <table className="mt-1 w-full text-xs">
          <thead><tr className="text-left text-slate-400"><th className="py-1 pr-2">Compte</th><th className="pr-2">Libellé</th><th className="pr-2">Partenaire</th><th className="pr-2 text-right">Débit</th><th className="text-right">Crédit</th></tr></thead>
          <tbody>{lignes.map((l, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="py-1 pr-2 font-mono">{l.compte}</td><td className="pr-2">{l.libelle}</td><td className="pr-2">{l.partenaire || ""}</td>
              <td className="pr-2 text-right tabular-nums">{l.debit ? euros(l.debit) : ""}</td><td className="text-right tabular-nums">{l.credit ? euros(l.credit) : ""}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <div className="mt-2 text-xs text-slate-600">
      <p>Pour <b>{action.pour || "Élodie"}</b> · sur {action.modele === "piece" ? "la pièce" : "la fiche"} <b>{action.reference}</b> · échéance {action.echeance || "aujourd'hui"}</p>
      <p className="mt-1 font-medium text-slate-800">{action.resume}</p>
      {action.note && <p className="mt-1 whitespace-pre-wrap">{action.note}</p>}
    </div>
  );
}

function CarteAction({ a, token, onFait }) {
  const [edition, setEdition] = useState(false);
  const [json, setJson] = useState(JSON.stringify(a.action, null, 2));
  const [envoi, setEnvoi] = useState(false);
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const valider = async () => {
    let action;
    try { action = edition ? JSON.parse(json) : undefined; } catch { return toast.error("JSON invalide"); }
    setEnvoi(true);
    try {
      const r = await fetch(`${API_URL}/api/agent/odoo-actions/${a.id}/valider`, { method: "POST", headers: h, body: JSON.stringify({ action }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Refusé");
      toast.success(d.resultat?.piece ? `Créé dans Odoo : ${d.resultat.piece} (brouillon)` : "Fait dans Odoo");
      onFait();
    } catch (e) { toast.error(e.message); onFait(); } finally { setEnvoi(false); }
  };
  const rejeter = async () => {
    await fetch(`${API_URL}/api/agent/odoo-actions/${a.id}/rejeter`, { method: "POST", headers: h });
    onFait();
  };

  return (
    <div className={`rounded-xl border bg-white p-4 ${a.statut === "erreur" ? "border-red-200" : "border-amber-200"}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-slate-500">{a.source} · {new Date(a.created_at).toLocaleString("fr-FR")}</p>
          <p className="mt-0.5 font-semibold text-slate-900">{a.type === "od" ? "Écriture d'OD (brouillon)" : "Tâche Odoo"}</p>
        </div>
        <button onClick={() => setEdition(!edition)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600"><Pencil className="h-3.5 w-3.5" /> {edition ? "Aperçu" : "Corriger"}</button>
      </div>
      {edition ? <textarea value={json} onChange={(e) => setJson(e.target.value)} rows={12} className="mt-2 w-full rounded-lg border border-slate-300 p-2 font-mono text-xs" />
        : <DetailAction action={a.action} />}
      {a.statut === "erreur" && <p className="mt-2 flex items-start gap-1.5 text-xs text-red-600"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{a.erreur}</p>}
      <div className="mt-3 flex gap-2">
        <button onClick={valider} disabled={envoi} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
          {envoi ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Valider et exécuter
        </button>
        <button onClick={rejeter} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs text-red-600"><X className="h-3.5 w-3.5" /> Rejeter</button>
      </div>
    </div>
  );
}

export default function ValidationsPage() {
  const { token } = useAuth();
  const [actions, setActions] = useState(null);
  const [compte, setCompte] = useState({});
  const [externes, setExternes] = useState([]);
  const h = { Authorization: `Bearer ${token}` };

  const charger = useCallback(async () => {
    const [a, c, x] = await Promise.all([
      fetch(`${API_URL}/api/agent/odoo-actions`, { headers: h }).then((r) => r.json()),
      fetch(`${API_URL}/api/agent/validations/compte`, { headers: h }).then((r) => r.json()),
      fetch(`${API_URL}/api/agent/actions-externes`, { headers: h }).then((r) => r.json()).catch(() => []),
    ]);
    setActions(Array.isArray(a) ? a : []); setCompte(c || {}); setExternes(Array.isArray(x) ? x : []);
    // eslint-disable-next-line
  }, [token]);
  useEffect(() => { charger(); }, [charger]);

  const attente = (actions || []).filter((a) => ["a_valider", "erreur"].includes(a.statut));
  const faites = (actions || []).filter((a) => a.statut === "execute");
  const tuiles = [
    { cle: "emails", libelle: "Emails rédigés", icone: Mail, lien: "/dashboard/emails" },
    { cle: "taches", libelle: "Tâches proposées", icone: CheckSquare2, lien: "/dashboard/tasks?focus=validation" },
    { cle: "memoire", libelle: "Souvenirs à retenir", icone: Brain, lien: "/dashboard/memoire" },
  ];

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <h1 className="text-2xl font-bold text-slate-900">À valider</h1>
        <p className="mt-1 text-sm text-slate-500">Tout ce que les agents te soumettent, et ce qu'ils ont fait eux-mêmes dans Odoo.</p>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {tuiles.map((t) => (
            <Link key={t.cle} to={t.lien} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-300">
              <t.icone className="h-5 w-5 text-indigo-600" />
              <span className="flex-1 text-sm text-slate-700">{t.libelle}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-sm font-bold ${compte[t.cle] ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-400"}`}>{compte[t.cle] || 0}</span>
            </Link>
          ))}
        </div>

        <section className="mt-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-amber-700">Actions Odoo à valider · {attente.length}</h2>
          {!actions ? <Loader2 className="mt-3 h-5 w-5 animate-spin text-slate-400" /> : !attente.length ? (
            <p className="mt-2 text-sm text-slate-500">Rien en attente.</p>
          ) : <div className="mt-2 grid gap-3 xl:grid-cols-2">{attente.map((a) => <CarteAction key={a.id} a={a} token={token} onFait={charger} />)}</div>}
        </section>

        <section className="mt-8">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Fait par les agents sur Meta, Gmail, GitHub, Stripe</h2>
          {!externes.length ? <p className="mt-2 text-sm text-slate-500">Aucune action pour l'instant.</p> : (
            <div className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
              {externes.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.erreur ? "bg-red-100 text-red-700" : "bg-violet-100 text-violet-700"}`}>{a.connexion} · {a.commande}</span>
                  <span className="min-w-0 flex-1 truncate text-slate-700">{a.erreur ? `Refusé : ${a.erreur}` : JSON.stringify(a.resultat).slice(0, 180)}</span>
                  <span className="text-xs text-slate-400">{a.source} · {new Date(a.created_at).toLocaleString("fr-FR")}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Fait dans Odoo</h2>
          {!faites.length ? <p className="mt-2 text-sm text-slate-500">Aucune action pour l'instant.</p> : (
            <div className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
              {faites.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-2 px-4 py-3 text-sm">
                  {a.direct && <span title="Fait directement par l'agent pendant une conversation" className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700"><Zap className="h-3 w-3" />direct</span>}
                  <span className="text-slate-800">{a.resume}</span>
                  <span className="text-xs text-slate-400">· {a.source} · {new Date(a.execute_at || a.updated_at).toLocaleString("fr-FR")}</span>
                  {a.resultat?.piece && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs">{a.resultat.piece}</span>}
                  {a.resultat?.lien && <a href={a.resultat.lien} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-xs text-indigo-600">Ouvrir dans Odoo <ExternalLink className="h-3 w-3" /></a>}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </CRMLayout>
  );
}
